import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import "@/lib/auth";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { ArrowUpRight, ShoppingBag, Check } from "lucide-react";

const CATEGORY_TABS = [
  { id: "all", label: "All" },
  { id: "Single (1 Song)", label: "Single" },
  { id: "EP (2–6 Songs)", label: "EP" },
  { id: "LP (7–10 Songs)", label: "LP" },
  { id: "Album (11–20 Songs)", label: "Album" },
  { id: "Vocal Lessons", label: "Vocal Lessons" },
  { id: "Consultation", label: "Consultation" },
  { id: "Digital", label: "Digital" },
];

export default function Store() {
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("all");
  const [email, setEmail] = useState("");
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (active === "all") return products;
    return products.filter((p) => (p.scope || "") === active);
  }, [products, active]);

  const grouped = useMemo(() => {
    const groups = {};
    for (const p of filtered) {
      const key = p.scope || "Other";
      groups[key] = groups[key] || [];
      groups[key].push(p);
    }
    // order
    const order = ["Single (1 Song)", "EP (2–6 Songs)", "LP (7–10 Songs)", "Album (11–20 Songs)", "Vocal Lessons", "Digital", "Consultation"];
    return order
      .filter((k) => groups[k])
      .map((k) => [k, groups[k]])
      .concat(Object.entries(groups).filter(([k]) => !order.includes(k)));
  }, [filtered]);

  const buy = async (product) => {
    setLoadingId(product.id);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/checkout/session", {
        product_id: product.id,
        origin_url: origin,
        customer_email: email || undefined,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="bg-warmwhite text-charcoal min-h-screen" data-testid="store-page">
      <Nav />

      <header className="pt-32 pb-12 bg-warmwhite border-b border-taupe/30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-[11px] uppercase tracking-widest-custom text-olive mb-6">Store</p>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tighter max-w-4xl" data-testid="store-title">
            Mixing, mastering &amp; <span className="italic text-gold">creative services.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-charcoal/70 leading-relaxed">
            Industry-quality mixing & mastering for singles, EPs, LPs and albums — designed to compete on streaming platforms, radio, playlists, and social media. Pick a package, check out securely, and we'll be in touch right away.
          </p>

          <div className="mt-10 flex flex-wrap gap-2" data-testid="store-tabs">
            {CATEGORY_TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                data-testid={`store-tab-${t.id.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={`px-5 py-2.5 text-[11px] uppercase tracking-widest-custom border transition-colors duration-300 ${
                  active === t.id ? "border-charcoal bg-charcoal text-warmwhite" : "border-taupe text-charcoal/70 hover:border-charcoal"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="mt-10 max-w-md">
            <label className="text-[10px] uppercase tracking-widest-custom text-charcoal/55">Email for receipt (optional)</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-2 w-full bg-transparent border-b border-taupe focus:border-charcoal outline-none py-2 font-serif text-lg"
              data-testid="store-email"
            />
          </div>
        </div>
      </header>

      <main className="py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 space-y-20">
          {grouped.map(([scope, items]) => (
            <section key={scope} data-testid={`store-section-${scope.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <div className="flex items-baseline justify-between mb-8 pb-4 border-b border-taupe/40">
                <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight">{scope}</h2>
                <p className="text-[11px] uppercase tracking-widest-custom text-charcoal/50">{items.length} {items.length === 1 ? "package" : "packages"}</p>
              </div>

              {scope === "Vocal Lessons" && <VocalLessonsIntro />}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {items.map((p) => (
                  <div key={p.id} className="group border border-taupe/40 bg-warmwhite p-7 md:p-8 flex flex-col hover:border-charcoal transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl shadow-charcoal/5" data-testid={`product-card-${p.id}`}>
                    <div className="flex items-start justify-between">
                      <p className="text-[10px] uppercase tracking-widest-custom text-olive">{p.tier || p.category}</p>
                      <ShoppingBag className="w-4 h-4 text-charcoal/40" strokeWidth={1.2} />
                    </div>
                    <h3 className="mt-4 font-serif text-2xl leading-tight">{p.name}</h3>
                    <p className="mt-3 text-sm text-charcoal/70 leading-relaxed flex-1">{p.description}</p>

                    <div className="mt-8 flex items-baseline gap-2">
                      {p.price === 0 ? (
                        <span className="font-serif text-3xl text-olive">Free</span>
                      ) : (
                        <>
                          <span className="font-serif text-4xl">${Number(p.price).toLocaleString()}</span>
                          <span className="text-xs text-charcoal/50 uppercase tracking-widest-custom">USD</span>
                        </>
                      )}
                    </div>

                    {p.delivery && (
                      <p className="mt-3 text-[11px] text-charcoal/55 leading-relaxed">{p.delivery}</p>
                    )}

                    <button
                      onClick={() => buy(p)}
                      disabled={loadingId === p.id}
                      className="mt-7 inline-flex items-center justify-center gap-2 bg-charcoal text-warmwhite px-6 py-4 text-[11px] uppercase tracking-widest-custom hover:bg-gold hover:text-charcoal transition-colors duration-300 disabled:opacity-60"
                      data-testid={`product-buy-${p.id}`}
                    >
                      {loadingId === p.id ? "Loading…" : (p.price === 0 ? "Request" : "Buy now")}
                      <ArrowUpRight className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-24">
          <div className="border-t border-taupe/40 pt-12 grid grid-cols-1 md:grid-cols-3 gap-10">
            <Trust label="Industry sound" body="Professional mix & master, vocal tuning, stereo enhancement, loudness optimization." />
            <Trust label="Streaming-ready" body="Optimized for Spotify, Apple Music, YouTube, and broadcast." />
            <Trust label="Direct communication" body="You work with me — not a junior. Revisions included by tier." />
          </div>
        </div>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}

function Trust({ label, body }) {
  return (
    <div className="flex items-start gap-4">
      <Check className="w-5 h-5 text-gold mt-1 shrink-0" strokeWidth={1.5} />
      <div>
        <p className="text-[11px] uppercase tracking-widest-custom text-charcoal/55">{label}</p>
        <p className="mt-2 font-serif text-xl leading-snug">{body}</p>
      </div>
    </div>
  );
}

function VocalLessonsIntro() {
  const bullets = [
    "Proper singing technique — posture, breathing, and healthy voice placement",
    "Vocal control — pitch, tone, range, and consistency across styles",
    "Effective warm-ups that prepare and protect your voice long-term",
  ];
  return (
    <div className="mb-10 grid grid-cols-1 md:grid-cols-5 gap-8 border border-taupe/40 bg-warmwhite p-8 md:p-10" data-testid="vocal-lessons-intro">
      <div className="md:col-span-2">
        <p className="text-[11px] uppercase tracking-widest-custom text-olive mb-4">Vocal Lessons · 1-on-1</p>
        <h3 className="font-serif text-3xl md:text-4xl font-light leading-tight tracking-tight">
          Build confidence, <span className="italic text-gold">strengthen your voice</span>, and improve your singing.
        </h3>
        <p className="mt-5 text-sm text-charcoal/70 leading-relaxed">
          Personalized 60-minute vocal lessons — tailored to your voice, experience level, and musical goals. Whether you're stepping up to your first mic or refining an established sound, every session moves you forward.
        </p>
      </div>
      <div className="md:col-span-3">
        <p className="text-[10px] uppercase tracking-widest-custom text-charcoal/55 mb-4">What you'll learn</p>
        <ul className="space-y-4">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-3 text-base text-charcoal/85 leading-relaxed">
              <Check className="w-5 h-5 text-gold mt-0.5 shrink-0" strokeWidth={1.5} />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <p className="mt-6 text-xs text-charcoal/55 leading-relaxed">
          All lessons are <strong className="text-charcoal">60 minutes</strong>. Monthly packages are based on four weeks per month. Weekly packages are billed at $50/hour.
        </p>
      </div>
    </div>
  );
}
